import { createDeduplicate, linearInterpolate, memoize } from "./_common";
import { AxialPoint } from "./AxialPoint";
import { CubePoint } from "./CubePoint";
import { PixelPoint } from "./PixelPoint";
import { POLYGON_RADIUS } from "./Shape";
import { Record, Set } from "immutable";
const OffsetPointCtor = Record({ type: 'IOffsetPoint', x: 0, y: 0 });
export var OffsetPoint;
(function (OffsetPoint) {
    OffsetPoint.create = (pointOrX, y) => OffsetPointCtor((typeof pointOrX === 'number')
        ? ({ x: pointOrX, y: y })
        : pointOrX);
    // Arithmetic
    OffsetPoint.add = (p1, p2) => OffsetPoint.create({
        x: p1.x + p2.x,
        y: p1.y + p2.y,
    });
    OffsetPoint.negate = ({ x, y }) => OffsetPoint.create({ x: -x, y: -y });
    OffsetPoint.subtract = (p1, p2) => OffsetPoint.add(p1, OffsetPoint.negate(p2));
    // Comparison
    OffsetPoint.equals = (p1, p2) => p1.x === p2.x && p1.y === p2.y;
    // Vectors
    OffsetPoint.clampX = ({ x }) => OffsetPoint.create({ x, y: 0 });
    OffsetPoint.clampY = ({ y }) => OffsetPoint.create({ x: 0, y });
    // Conversions
    OffsetPoint.toAxialPoint = ({ x, y }) => AxialPoint.create({
        q: x,
        r: y - (x - (x & 1)) / 2,
    });
    OffsetPoint.toCubePoint = (point) => AxialPoint.toCubePoint(OffsetPoint.toAxialPoint(point));
    OffsetPoint.toPixelPoint = ({ x, y }) => ({
        x: POLYGON_RADIUS * 3 / 2 * x,
        y: POLYGON_RADIUS * Math.sqrt(3) * (y + 0.5 * (x & 1))
    });
    // Constraints
    OffsetPoint.round = (point) => CubePoint.toOffsetPoint(CubePoint.round(OffsetPoint.toCubePoint(point)));
    OffsetPoint.floor = ({ x, y }) => OffsetPoint.create({ x: Math.floor(x), y: Math.floor(y) });
    OffsetPoint.ceil = ({ x, y }) => OffsetPoint.create({ x: Math.ceil(x), y: Math.ceil(y) });
    // Spatiality
    OffsetPoint.getDistance = (p1, p2) => (CubePoint.getDistance(OffsetPoint.toCubePoint(p1), OffsetPoint.toCubePoint(p2)));
    OffsetPoint.interpolateLinear = (p1, p2, percentile) => OffsetPoint.create({
        x: linearInterpolate(p1.x, p2.x, percentile),
        y: linearInterpolate(p1.y, p2.y, percentile),
    });
    // Aggregation
    OffsetPoint.allPointsWithinRange = (center, distance) => (Set([...CubePoint.allPointsWithinRange(OffsetPoint.toCubePoint(center), distance)].map(CubePoint.toOffsetPoint)));
    OffsetPoint.toCacheKey = ({ x, y }, precision) => {
        const xKey = precision ? Math.round(x * Math.pow(10, precision)) : x;
        const yKey = precision ? Math.round(y * Math.pow(10, precision)) : y;
        return `${xKey}:${yKey}`;
    };
    OffsetPoint.deduplicate = createDeduplicate(OffsetPoint.toCacheKey);
    OffsetPoint.pointsAlongLine = (p1, p2, opts = {}) => (CubePoint.pointsAlongLine(OffsetPoint.toCubePoint(p1), OffsetPoint.toCubePoint(p2), opts).map(CubePoint.toOffsetPoint));
    OffsetPoint.pointsAlongPixelLine = (p1, p2, opts = {}) => (CubePoint.pointsAlongPixelLine(p1, p2, opts).map(CubePoint.toOffsetPoint));
    OffsetPoint.pointsWithinRect = memoize((topLeft, bottomRight, _) => `OffsetPoint.pointsWithinRect:${OffsetPoint.toCacheKey(topLeft)}:${OffsetPoint.toCacheKey(bottomRight)}`, (topLeft, bottomRight, depth = 0) => {
        if (OffsetPoint.equals(topLeft, bottomRight))
            return Set([topLeft]);
        const midpoint = OffsetPoint.interpolateLinear(topLeft, bottomRight, 0.5);
        const midwayVector = OffsetPoint.subtract(midpoint, topLeft);
        const midLeft = OffsetPoint.add(topLeft, OffsetPoint.clampY(midwayVector));
        const midTop = OffsetPoint.add(topLeft, OffsetPoint.clampX(midwayVector));
        const midRight = OffsetPoint.subtract(bottomRight, OffsetPoint.clampY(midwayVector));
        const midBottom = OffsetPoint.subtract(bottomRight, OffsetPoint.clampX(midwayVector));
        return Set().union(OffsetPoint.pointsWithinRect(topLeft, OffsetPoint.floor(midpoint), depth + 1), OffsetPoint.pointsWithinRect(OffsetPoint.ceil(midpoint), bottomRight, depth + 1), OffsetPoint.pointsWithinRect(OffsetPoint.ceil(midTop), OffsetPoint.floor(midRight), depth + 1), OffsetPoint.pointsWithinRect(OffsetPoint.ceil(midLeft), OffsetPoint.floor(midBottom), depth + 1));
    });
    OffsetPoint.pointsWithinPixelRect = (corner1, corner2) => {
        const { topLeft, bottomRight } = PixelPoint.toBoundingRect(corner1, corner2);
        return OffsetPoint.pointsWithinRect(PixelPoint.toRoundedOffsetPoint(topLeft), PixelPoint.toRoundedOffsetPoint(bottomRight));
    };
})(OffsetPoint || (OffsetPoint = {}));
//# sourceMappingURL=OffsetPoint.js.map